import React, { useEffect, useMemo, useRef, useState } from 'react';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { Autocomplete, Box, Button, Grid, Stack, TextField } from '@mui/material';
import AddRefundRequest from './AddRefundRequest';
import GlobalDatagrid from '../../components/mui/DataGrid/GlobalDatagrid';
import {
  REFUND_REQUEST_TABLE_HEAD,
  TableHead_Refund_fn,
} from '../../components/datagrid/refund/RefundRequestTableHead';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react/dist/iconify.js';
import ViewRefundDetails from './ViewRefundDetails';
import GlobalRefundApprovalReject from './GlobalRefundApprovalReject';
import RequestStats from './RequestStats';
import ToolTip from '../../components/ToolTip/ToolTip';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  getEnrolledRefundRequest,
  getEnrolledRefundRequestFinance,
  getNonEnrolledRefundRequest,
} from '../../redux/features/refundSlice';
import GlobalSelectFieldWithoutFormikNew from '../../components/mui/GlobalSelectFieldWithoutFormikNew';
import FromAndToDatePicker from '../../components/mui/FromAndToDatePicker';
import dayjs from 'dayjs';
import ReasonEdit from './verify_approve/components/ReasonEdit';
import TutorsTable from './verify_approve/components/TutorsTable';
import DetailsAction from './verify_approve/components/DetailsAction';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import {
  refundGlobalStatus,
  refundStatusBy,
  refundStatusType,
  typeOfStudent,
} from '../../utils/constants/GlobalStaticData';
import ChatDrawer from '../chatSystem/ChatDrawer';
import { openChat, setRefreashNeeded } from '../../redux/features/chatSlice';

const nonEnrollmentRefundStatus = [
  { id: -1, name: 'All' },
  { id: 0, name: 'Pending' },
  { id: 1, name: 'Approved' },
  { id: 2, name: 'Rejected' },
];

const RefundRequestFinance = () => {
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { data: tableRow, meta } = useSelector((state) => state.refund.refundRequestTable);

  const { gridSize, pageNum, rowData, openAnchor, search } = paginationData;
  const { loading } = useSelector((state) => state.refund);
  const dispatch = useDispatch();

  const [selectedType, setSelectedType] = useState(typeOfStudent[0]);
  const [selectedRoleStatus, setSelectedRoleStatus] = useState({});
  const [selectedStatusType, setSelectedStatusType] = useState({});

  const [fromDate, setFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD')); //DEFAULT SET TO CURRENT MONTH STARTING DATE TO ENDING DATE
  const [toDate, setToDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [openTutorsTable, setOpenTutorsTable] = useState(false);
  const [openRejectionReason, setOpenRejectionReason] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [openDetails, setOpenDetails] = useState(false);

  const [action, setViewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [approvalModal, setApprovalModal] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { open: chatOpenFromRedux, refreashNeeded } = useSelector((state) => state.chat);
  const prevOpenRef = useRef(chatOpenFromRedux);

  const navigate = useNavigate();

  const stableStatusDeps = useMemo(() => {
    const statusBy = selectedRoleStatus?.id;
    const statusType = selectedStatusType?.id;
    if(statusBy==undefined || statusBy==null){
      setSelectedStatusType({});
    }

    // Only create a dependency when BOTH exist (0 allowed)
    if (statusBy !== undefined && statusType !== undefined) {
      return `${statusBy}-${statusType}`;
    }

    return null;
  }, [selectedRoleStatus?.id, selectedStatusType?.id]);
  const dispatchParams = useMemo(() => {
    return {
      gridSize,
      pageNum,
      search,
      fromDate,
      toDate,
      status: null,
      type: 2,
      statusBy: selectedRoleStatus?.id,
      statusType: selectedStatusType?.id,
    };
  }, [pageNum, gridSize, search, fromDate, toDate, stableStatusDeps]);

  useEffect(() => {
    dispatch(
      selectedType?.id ? getNonEnrolledRefundRequest(dispatchParams) : getEnrolledRefundRequestFinance(dispatchParams),
    );
  }, [dispatch, selectedType, dispatchParams]);

  useEffect(() => {
    if (!chatOpen || !selectedRow) return;
    dispatch(
      openChat({
        chatRoomId: selectedRow?.chat_room_id || null,
        groupCreationUsersList: selectedRow?.related_users || [],
        groupName: `Refund - ${selectedRow?.student_name}-${selectedRow?.expense_id}`,
        rowDetails: selectedRow,
      }),
    );
    setChatOpen(false);
  }, [chatOpen]);

  useEffect(() => {
    const justClosed = prevOpenRef.current === true && chatOpenFromRedux === false;
    if (justClosed && refreashNeeded) {
      dispatch(
        selectedType?.id
          ? getNonEnrolledRefundRequest(dispatchParams)
          : getEnrolledRefundRequestFinance(dispatchParams),
      );
      dispatch(setRefreashNeeded(false));
    }
    prevOpenRef.current = chatOpenFromRedux;
  }, [chatOpenFromRedux, dispatchParams, selectedType, refreashNeeded]);

  const GLOBAL_DIALOGUE = [
    {
      open: Boolean(openTutorsTable),
      setOpen: setOpenTutorsTable,
      title: 'Tutors',
      content: <TutorsTable ids={openTutorsTable} />,
    },
    {
      open: openRejectionReason,
      title: 'Bank Account Details',
      setOpen: setOpenRejectionReason,
      content: (
        <ReasonEdit
          setOpen={setOpenTutorsTable}
          non={Boolean(selectedType?.id)}
          dispatchParams={dispatchParams}
          submitDispatch={selectedType?.id ? getNonEnrolledRefundRequest : getEnrolledRefundRequest}
          open={openRejectionReason}
        />
      ),
    },
    {
      open: Boolean(openDetails),
      setOpen: setOpenDetails,
      title: 'Details',
      content: (
        <DetailsAction
          action
          data={openDetails}
          setOpen={setOpenDetails}
          dispatchParams={dispatchParams}
          submitDispatch={getNonEnrolledRefundRequest}
        />
      ),
    },
  ];

  const columns = useMemo(
    () =>
      TableHead_Refund_fn({
        setOpenTutorsTable,
        setOpenRejectionReason,
        selectedType: selectedType?.id,
        setOpenDetails,
        setApprovalModal,
        setSelectedRow,
        setApprovalModal,
        setEditReason,
        approvalOrVerify: true,
        setChatOpen,
        isFinance: true,
      }),
    [selectedType],
  );

  return (
    <GlobalWrapper title={'Refund Request'}>
      <Box sx={{ width: '100%' }}>
        <Box>
          <RequestStats requestStats={meta} loading={loading} />
        </Box>
        <Grid container spacing={2} my={1}>
          <Grid item xs={12} md={6} lg={4}>
            <FromAndToDatePicker
              disabled={false}
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
              fullWidth={true}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={2.6}>
            <GlobalSelectFieldWithoutFormikNew
              keys={{ itemId: 'id', itemValue: 'name' }}
              data={typeOfStudent}
              label={'Student Type'}
              fullWidth={true}
              selectedDetails={selectedType}
              setChange={setSelectedType}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={2.6}>
            <GlobalSelectFieldWithoutFormikNew
             disabled={selectedType?.id==1}
              keys={{ itemId: 'id', itemValue: 'name' }}
              data={refundStatusBy}
              label={'Role Wise Filter'}
              fullWidth={true}
              selectedDetails={selectedRoleStatus}
              setChange={setSelectedRoleStatus}
              clearButton={true}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={2.6}>
            <GlobalSelectFieldWithoutFormikNew
              disabled={!selectedRoleStatus?.id || selectedType?.id==1}
              keys={{ itemId: 'id', itemValue: 'name' }}
              data={refundStatusType}
              label={'Role Wise Status'}
              fullWidth={true}
              selectedDetails={selectedStatusType}
              setChange={setSelectedStatusType}
              // clearButton={true}
            />
          </Grid>
        </Grid>
        <Box mb={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<Icon icon="lsicon:calendar-filled" />}
            onClick={() => navigate('/my-workspace/scheduled-refund-payments')}
          >
            Scheduled Payments
          </Button>
        </Box>

        <GlobalDatagrid
          tableHead={columns}
          rows={tableRow ?? []}
          meta={meta?.totalRecords || 0}
          loading={loading}
          rowUniqueId="sl_no"
          rowClick={false}
        />
        {/* {action && selectedRow && <ViewRefundDetails open={action} setOpen={setViewOpen} row={selectedRow} />} */}
        {approvalModal && (
          <GlobalRefundApprovalReject
            open={approvalModal}
            setOpen={setApprovalModal}
            rowData={selectedRow}
            dispatchParams={dispatchParams}
            loading={loading}
            action={'finance'}
            apiDispatchAfterSuccess={getEnrolledRefundRequestFinance}
          />
        )}
        {GLOBAL_DIALOGUE.map(({ open, content, setOpen, title }, i) => (
          <GlobalDialog
            key={i}
            maxWidth={'100%'}
            content={content}
            open={open}
            setOpen={setOpen}
            closeButton={true}
            title={title}
          />
        ))}
      </Box>
    </GlobalWrapper>
  );
};

export default RefundRequestFinance;
